/**
 * Matches the origin of a browser-extension script (scheme plus extension id).
 * A script injected into the page's main world keeps this origin in stack
 * traces and in `ErrorEvent.filename`, which is what lets us tell extension
 * code apart from our own bundle, and one extension apart from another.
 */
const EXTENSION_ORIGIN_PATTERN =
  /(?:chrome|moz|safari-web|safari|ms-browser)-extension:\/\/[a-z0-9-]+/i;

/**
 * Matches a single stack-trace frame, skipping the leading `Name: message`
 * header that V8 puts on the first line. Covers both the V8 shape
 * (`    at fn (url:1:2)`) and the SpiderMonkey/JavaScriptCore shape
 * (`fn@url:1:2`, including the anonymous `@url:1:2`).
 */
const STACK_FRAME_PATTERN = /^\s*(?:at\s|[^\s@]*@[^\s@])/;

/**
 * Returns the browser-extension origin an error was thrown from, or undefined
 * when the error is not attributable to an extension.
 *
 * Only the topmost (throwing) frame is considered, so an error raised by our
 * code is still attributed to us even when an extension appears further down
 * the stack — for example when an extension has patched `fetch` and our
 * handler throws inside its callback. When neither argument carries usable
 * information the result is undefined, so an unattributable error is treated
 * as ours rather than silently blamed on an extension.
 *
 * @param stack The error's stack trace. When omitted or not a string, only
 *   `filename` is consulted.
 * @param filename `ErrorEvent.filename`, i.e. the script the error came from.
 *   When omitted, only `stack` is consulted. This is the only usable signal
 *   for cross-origin errors, where the browser withholds the stack.
 * @return The extension origin (e.g. `chrome-extension://abc123`), or
 *   undefined when the error is not attributable to an extension.
 * @throws Never; all inputs are treated as untrusted and narrowed internally.
 */
export function browserExtensionOrigin({
  stack,
  filename,
}: {
  stack?: unknown;
  filename?: unknown;
}): string | undefined {
  if (typeof filename === 'string') {
    const match = EXTENSION_ORIGIN_PATTERN.exec(filename);
    if (match) {
      return match[0];
    }
  }
  if (typeof stack !== 'string') {
    return undefined;
  }
  const topFrame = stack
    .split('\n')
    .find(line => STACK_FRAME_PATTERN.test(line));
  if (topFrame === undefined) {
    return undefined;
  }
  return EXTENSION_ORIGIN_PATTERN.exec(topFrame)?.[0];
}

/**
 * Returns the browser-extension origin of the first `console.error` argument
 * that carries an extension stack trace. Used by the production console
 * interceptor, which receives already-serialized `console.error` arguments
 * rather than live `Error` instances.
 *
 * @param loggedArgs The serialized `console.error` arguments to inspect. An
 *   empty array returns undefined.
 * @return The extension origin, or undefined when no argument is attributable
 *   to an extension.
 * @throws Never; non-object arguments are ignored.
 */
export function browserExtensionArgOrigin(
  loggedArgs: unknown[],
): string | undefined {
  for (const arg of loggedArgs) {
    if (typeof arg === 'object' && arg !== null && 'stack' in arg) {
      const origin = browserExtensionOrigin({stack: arg.stack});
      if (origin !== undefined) {
        return origin;
      }
    }
  }
  return undefined;
}

/**
 * Creates a gate that reports the first error seen from each browser
 * extension and drops every later one from the same extension.
 *
 * An extension stuck in a throw loop otherwise bills us one `logError`
 * invocation per throw. Dropping extension errors outright would instead cost
 * us all knowledge of which extensions break on our site, so the first report
 * per extension is kept: enough to see the population, capped at one event
 * each. State is per page session, so a reload reports each extension again.
 *
 * @return A gate whose methods report whether an error should be dropped.
 * @throws Never.
 */
export function createBrowserExtensionErrorGate(): {
  /**
   * Reports whether an error raised on `window` should be dropped.
   *
   * @param stack The error's stack trace, when available.
   * @param filename `ErrorEvent.filename`, when available. Required to
   *   attribute a cross-origin error whose stack the browser withholds.
   * @return True when this extension has already been reported this session.
   */
  shouldDrop(args: {stack?: unknown; filename?: unknown}): boolean;
  /**
   * Reports whether a set of `console.error` arguments should be dropped.
   *
   * @param loggedArgs The serialized `console.error` arguments.
   * @return True when this extension has already been reported this session.
   */
  shouldDropLoggedArgs(loggedArgs: unknown[]): boolean;
} {
  const reported = new Set<string>();
  const admit = (origin: string | undefined): boolean => {
    if (origin === undefined) {
      return false;
    }
    if (reported.has(origin)) {
      return true;
    }
    reported.add(origin);
    return false;
  };
  return {
    shouldDrop: args => admit(browserExtensionOrigin(args)),
    shouldDropLoggedArgs: loggedArgs =>
      admit(browserExtensionArgOrigin(loggedArgs)),
  };
}
